"use client";

import { LocationPinIcon } from "icons/react-icons/LocationPin";
import Link from "next/link";
import React from "react";
import {
  Heading,
  ImageWrapper,
  LinkButton,
  Spinner,
  Text,
} from "ui-components";

import {
  JobResponseEntity,
  SimilarJobItem,
  SimilarJobsResponse,
  useJobGetSimilarJobs,
} from "@/hooks/api/generated";
import { useAuth } from "@/hooks/useAuth";
import ToggleSaveJob from "@/src/app/[locale]/(public)/jobs/[id]/Components/ToggleSaveJob";
import { countries } from "@/src/constants/conutries";
import { capitalize, concatString } from "@/utils/string";

function SimilarJobs({
  jobDetails,
}: {
  jobDetails: JobResponseEntity;
}): JSX.Element {
  const { isAuthenticated, authData } = useAuth();
  const userId = authData?.id ? String(authData.id) : undefined;
  const { data: similarJobs, status } =
    useJobGetSimilarJobs<SimilarJobsResponse>(
      jobDetails.slug,
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        userId: userId as any,
      },
      {
        query: {
          queryKey: ["useJobGetSimilarJobs", jobDetails.slug, userId],
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          refetchOnReconnect: false,
          // Wait for auth to resolve so we don't fire once without userId
          // and again after AuthProvider hydrates the logged-in user.
          enabled: isAuthenticated !== undefined && !!jobDetails.slug,
        },
      }
    );

  const location = jobDetails.locations?.location;
  const search = jobDetails.occupations?.[0]?.occupation?.name;

  const params = new URLSearchParams({
    similar: "true",
  });

  if (search) {
    params.append("search", search);
  }
  if (location) {
    params.append(
      "location",
      `${location}, ${countries[jobDetails?.locations?.country]}`
    );
  }
  const href = `/jobs?${params.toString()}`;

  return (
    <div className="flex flex-col gap-4">
      <Heading variant="h2">Similar Jobs</Heading>
      {status === "pending" && (
        <div className="flex justify-center my-5">
          <Spinner size="lg" intent="brand-primary" />
        </div>
      )}
      {similarJobs && similarJobs?.data?.length === 0 && (
        <div className="flex justify-center my-5">No similar jobs found.</div>
      )}
      {similarJobs &&
        similarJobs?.data?.length > 0 &&
        similarJobs?.data?.map((job: SimilarJobItem) => (
          <div key={job.id}>
            <div className="border border-gray-200 hover:border-orange-500 rounded-[10px] px-3 py-4 flex gap-4 items-start">
              <ImageWrapper
                src={job?.organization.logo_url ?? undefined}
                width={68}
                height={68}
                alt={`${jobDetails?.organization?.name} logo`}
                className="rounded-[10px] w-[48px] h-[48px] md:w-[68px] md:h-[68px] border"
                style={{
                  objectFit: "cover",
                }}
              />
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <div className="flex items-start justify-between w-full">
                  <Text className="font-semibold text-sm">
                    {job.organization.name}
                  </Text>
                  <div className="w-6 h-6 flex-shrink-0">
                    <ToggleSaveJob
                      jobDetails={job}
                      isInFloating
                      isCircle
                      useLocalState
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-4 w-full">
                  <Link
                    href={`/jobs/${job.slug}?utm_source=mygwork&utm_medium=website&utm_campaign=similar_jobs`}
                  >
                    <Text variant="large" className="font-semibold">
                      {concatString(job.title, 50)}
                    </Text>
                  </Link>
                  <div className="flex gap-2 items-center w-full overflow-hidden">
                    <LocationPinIcon className="w-6 h-6 flex-shrink-0" />
                    <Text className="text-sm truncate">
                      {concatString(
                        `${capitalize(
                          countries[job.locations?.country || ""] || ""
                        )}, ${job.locations?.location || ""}`,
                        33
                      )}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      {status !== "pending" && similarJobs && similarJobs?.data?.length > 0 && (
        <div className="flex justify-center mt-2">
          <LinkButton intent="secondary" href={href}>
            See More Jobs
          </LinkButton>
        </div>
      )}
    </div>
  );
}

export default SimilarJobs;
